import { useCallback } from 'react';
import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import FloorModel from '../../../models/standard/FloorModel';
import RoomModel from '../../../models/standard/RoomModel';
import SpaceModel from '../../../models/standard/SpaceModel';
import TilesModel from '../../../models/standard/TileModel';

export interface ITileActions {
  setFloorTiles: (tiles: TilesModel) => void;
  updateTileProperty: (property: string, value: number | string) => void;
  createTileIfNeeded: (length: number, width: number) => void;
  deleteTile: () => void;
}

// ---- helpers (legacy + new-structure safe) ----
const getFirstSpaceFloor = (b: IBuildingProviderContext): FloorModel | undefined => {
  const room0 = b.rooms?.[0] as any;
  const space0 = room0?.spaces?.[0] as any;
  return space0?.floor as FloorModel | undefined;
};

const withUpdatedFirstSpaceFloor = (
  prev: IBuildingProviderContext,
  nextFloor: FloorModel,
): IBuildingProviderContext => {
  const rooms = Array.isArray(prev.rooms) ? prev.rooms : [];
  if (rooms.length === 0) {
    // No rooms yet -> store legacy floor field if it exists in your type at runtime,
    // but do not crash. (We avoid inventing rooms here.)
    return { ...prev, floor: nextFloor } as any;
  }

  const r0 = rooms[0] as any;
  const spaces = Array.isArray(r0.spaces) ? (r0.spaces as any[]) : [];
  if (spaces.length === 0) {
    // Same: avoid inventing spaces; keep legacy floor compatibility if present.
    return { ...prev, floor: nextFloor } as any;
  }

  const s0 = spaces[0];
  const updatedSpace = new SpaceModel(
    nextFloor,
    s0.products ?? [],
    s0.elements ?? [],
    s0.models2D ?? [],
    s0.boundaries ?? [],
    s0.walls ?? [],
  );
  if (s0.uuid) updatedSpace.setUUID(s0.uuid);

  const newSpaces = spaces.slice();
  newSpaces[0] = updatedSpace;

  const updatedRoom = new RoomModel(r0.title ?? '', newSpaces, r0.attributes ?? []);
  if (r0.uuid) updatedRoom.setUUID(r0.uuid);

  const newRooms = rooms.slice();
  newRooms[0] = updatedRoom;

  return { ...prev, rooms: newRooms };
};

const ensureFloorModel = (maybeFloor: FloorModel | undefined, legacyFallback: any): FloorModel => {
  // Prefer space floor, then legacy building.floor, otherwise create empty floor
  const floor = maybeFloor ?? (legacyFallback as FloorModel | undefined);
  if (floor) return floor;

  // empty floor: no tiles, no texture, no attrs
  return new FloorModel(undefined, null, []);
};

export const useTileActions = (setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>): ITileActions => {
  const setFloorTiles = useCallback(
    (tiles: TilesModel): void => {
      setBuilding((prev) => {
        const currentFloor = ensureFloorModel(getFirstSpaceFloor(prev), (prev as any).floor);
        const nextFloor = new FloorModel(tiles, currentFloor.texture ?? null, currentFloor.attributes ?? [], currentFloor.uuid);
        const nextBuilding = withUpdatedFirstSpaceFloor(prev, nextFloor);

        return {
          ...nextBuilding,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.FLOOR_TILES_SET,
        };
      });
    },
    [setBuilding],
  );

  const updateTileProperty = useCallback(
    (property: string, value: number | string) => {
      setBuilding((prev) => {
        const currentFloor = ensureFloorModel(getFirstSpaceFloor(prev), (prev as any).floor);
        const currentTile = currentFloor.tiles;

        const length = currentTile?.length || 0;
        const width = currentTile?.width || 0;
        const rotation = currentTile?.rotation || 0;
        const jointWidth = currentTile?.jointWidth || 1;
        const jointColor = currentTile?.jointColor || '#000000';

        let updatedTile: TilesModel;

        switch (property) {
          case 'length':
            updatedTile = new TilesModel(value as number, width, rotation, jointWidth, jointColor);
            break;
          case 'width':
            updatedTile = new TilesModel(length, value as number, rotation, jointWidth, jointColor);
            break;
          case 'rotation':
            updatedTile = new TilesModel(length, width, value as number, jointWidth, jointColor);
            break;
          case 'jointWidth':
            updatedTile = new TilesModel(length, width, rotation, value as number, jointColor);
            break;
          case 'jointColor':
            updatedTile = new TilesModel(length, width, rotation, jointWidth, value as string);
            break;
          default:
            // Do not throw in production UI actions
            return prev;
        }

        const nextFloor = new FloorModel(updatedTile, currentFloor.texture ?? null, currentFloor.attributes ?? [], currentFloor.uuid);
        const nextBuilding = withUpdatedFirstSpaceFloor(prev, nextFloor);

        return {
          ...nextBuilding,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.FLOOR_TILES_CHANGED,
        };
      });
    },
    [setBuilding],
  );

  const createTileIfNeeded = useCallback(
    (length: number, width: number): void => {
      if (length <= 0 || width <= 0) return;

      setBuilding((prev) => {
        const currentFloor = ensureFloorModel(getFirstSpaceFloor(prev), (prev as any).floor);
        const currentTiles = currentFloor.tiles;

        if (currentTiles?.length === length && currentTiles?.width === width) {
          return prev;
        }

        const newTiles = new TilesModel(
          length,
          width,
          currentTiles?.rotation || 0,
          currentTiles?.jointWidth || 1,
          currentTiles?.jointColor || '#000000',
        );

        const nextFloor = new FloorModel(newTiles, currentFloor.texture ?? null, currentFloor.attributes ?? [], currentFloor.uuid);
        const nextBuilding = withUpdatedFirstSpaceFloor(prev, nextFloor);

        return {
          ...nextBuilding,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.FLOOR_TILES_CREATED,
        };
      });
    },
    [setBuilding],
  );

  const deleteTile = useCallback((): void => {
    setBuilding((prev) => {
      const currentFloor = ensureFloorModel(getFirstSpaceFloor(prev), (prev as any).floor);
      const nextFloor = new FloorModel(undefined, currentFloor.texture ?? null, currentFloor.attributes ?? [], currentFloor.uuid);
      const nextBuilding = withUpdatedFirstSpaceFloor(prev, nextFloor);

      return {
        ...nextBuilding,
        lastAction: EHistoryAction.FLOOR_TILES_DELETED,
        isSetByUndoRedo: false,
      };
    });
  }, [setBuilding]);

  return {
    setFloorTiles,
    updateTileProperty,
    createTileIfNeeded,
    deleteTile,
  };
};
